/**
 * Recursively removes undefined values before Firestore client writes.
 * Firestore rejects undefined anywhere in document payloads (browser SDK default).
 */

import { DocumentReference, Timestamp } from 'firebase/firestore';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  if (value instanceof Date) return false;
  if (value instanceof Timestamp) return false;
  if (value instanceof DocumentReference) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function shouldPreserveLeaf(value: unknown): boolean {
  if (value === undefined) return false;
  if (value === null) return true;
  if (typeof value !== 'object') return true;
  if (value instanceof Date) return true;
  if (value instanceof Timestamp) return true;
  if (value instanceof DocumentReference) return true;
  if (!isPlainObject(value) && !Array.isArray(value)) return true;
  return false;
}

/**
 * Removes undefined object properties recursively.
 * Undefined array entries are omitted (not converted to null).
 * Preserves false, 0, empty strings, null, Date, Timestamp, DocumentReference,
 * and non-plain class instances.
 */
export function sanitizeForFirestore<T>(value: T): T {
  if (value === undefined) {
    return value;
  }

  if (shouldPreserveLeaf(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    const next: unknown[] = [];
    for (const entry of value) {
      if (entry === undefined) continue;
      next.push(sanitizeForFirestore(entry));
    }
    return next as T;
  }

  if (isPlainObject(value)) {
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (entry === undefined) continue;
      next[key] = sanitizeForFirestore(entry);
    }
    return next as T;
  }

  return value;
}

/**
 * Build a plain object omitting keys whose values are undefined.
 * Use when constructing payloads instead of assigning undefined explicitly.
 */
export function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) next[key] = value;
  }
  return next as Partial<T>;
}
